import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { convertPriceStringToInteger } from "@shared/modules/financials/utils/price-utils";
import WalletElement from "@shared/modules/payments/components/WalletElement/WalletElement.web";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import { PurchaseData } from "@shared/modules/payments/types";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import AffirmPaymentType from "@app-contexts/public/shared/checkout/components/payment-type-accordion/AffirmPaymentType/AffirmPaymentType.web";
import {
  CardPaymentTypeManualCardEntry,
  CardPaymentTypeSavedCard,
} from "@app-contexts/public/shared/checkout/components/payment-type-accordion/CardPaymentType/CardPaymentType.web";
import PaymentTypeAccordion from "@app-contexts/public/shared/checkout/components/payment-type-accordion/PaymentTypeAccordion.web";
import { checkForReactNativeAttribute } from "@shared/hooks/platform/react-native";
import Skeleton from "@shared/components/ui-kit/feedback/Skeleton/Skeleton.web";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import useUserBasedPaymentMethodService from "@app-contexts/user/features/profile/data/services/useUserBasedPaymentMethodService";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { CURRENCY_MAP, PAYMENT_GATEWAY_MAP } from "@shared/constants/enums";
import { UserBasedUserPaymentMethodSerializer } from "@app-contexts/user/features/profile/data/types/payment-types";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import PublicRefundProtection from "../../../../PublicRefundProtection/PublicRefundProtection.web";

import type { CheckoutFormData } from "../../hooks/useCheckoutValidation";
import { VALIDATION_FIELDS } from "../../hooks/useCheckoutValidation";

// Payment type configuration
enum PaymentSubtype {
  MANUAL_CARD_ENTRY = "manual_card_entry",
  SAVED_CARD = "saved_card",
  AFFIRM = "affirm",
}

interface PaymentTypeOption {
  id: string; // Unique identifier for this payment option
  paymentType: number; // PAYMENT_TYPE_OPTIONS_MAP value
  subtype: PaymentSubtype;
  savedPaymentMethodId?: number; // For saved cards
  savedPaymentMethod?: UserBasedUserPaymentMethodSerializer; // For saved cards
}

// TODO refactor once we change our payment method layout to be horizontal.
// each payment type should be isolated, and we need to stop relying on indexes due to
// dynamically loaded data which means dropping Accordions, hence why
// a refactor is perfect in time for a layout change.
// Should also probably closely map to PAYMENT_MANAGER_CONFIRMATION_STRATEGY.
export function PaymentSectionRegularBaskets({
  venue,
  publicBasket,
  allowRedirect,
  checkoutForms,
  isBasketUpdating,
  enabledPaymentOptions,
  onValid,
  onInvalid,
  onBillingAddressChange,
}: {
  venue: PublicVenueSerializer | undefined;
  publicBasket: ReturnType<typeof usePublicBasket>;
  allowRedirect: boolean;
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  isBasketUpdating: boolean;
  enabledPaymentOptions: Record<number, boolean>;
  onValid: () => void;
  onInvalid: () => void;
  onBillingAddressChange: (billingAddress: {
    info_home_address_country: string;
  }) => void;
}) {
  const { t } = useTranslation("checkout");
  const { handleSetPurchaseData } = usePaymentManager();
  const { isFlagActive } = useDjangoWaffle();
  const { setValue, watch, formState } = useFormContext<CheckoutFormData>();

  const paymentTypeValid = watch(VALIDATION_FIELDS.paymentType);
  const refundProtectionValid = watch(VALIDATION_FIELDS.refundProtection);
  const validationAttempted = watch(VALIDATION_FIELDS.validationAttempted);

  const isPaymentTypeTouched = formState.touchedFields.paymentType ?? false;
  const isRefundProtectionTouched =
    formState.touchedFields.refundProtection ?? false;

  const paymentTypeErrorMessage = formState.errors.paymentType?.message;
  const refundProtectionErrorMessage =
    formState.errors.refundProtection?.message;

  const [isWalletReady, setIsWalletReady] = useState(false);
  const [isWalletSupported, setIsWalletSupported] = useState(true);
  /**
   * We have data that asynchronously loads (eg. wallets from processors, BNPLs from processors,
   * saved credit cards on our system).
   * This fallback timer is here so that in the event those things do not load in time,
   * then we always show manual card entry.
   */
  const [fallbackTimerTriggered, setFallbackTimerTriggered] = useState(false);

  const [selectedPaymentOptionId, setSelectedPaymentOptionId] = useState<
    string | undefined
  >(undefined);
  const [paymentValidationState, setPaymentValidationState] = useState<
    Record<string, boolean>
  >({});
  const hasInitializedWebviewWithManualCardEntry = useRef(false);
  const refundProtectionRef = useRef<HTMLDivElement>(null);
  const paymentMethodsRef = useRef<HTMLDivElement>(null);

  // Compute refund protection eligibility (basket characteristics only)
  const { supportsRefundProtection, paymentVenue } = usePublicBasket();
  const isBasketEligibleForProtection =
    supportsRefundProtection && // basket supports refund protection (items, not free, no credits, etc.)
    Boolean(publicBasket.paymentGateway?.is_system); // system payment gateway

  // Whether to show the widget (A/B test controls this)
  const shouldShowRefundProtectionWidget =
    isBasketEligibleForProtection &&
    isFlagActive("enable_refund_protection", paymentVenue);

  // Feature flag for auto-scroll to invalid sections during validation
  const isCheckoutScrollEnabled = isFlagActive("checkout_scroll_enabled");

  // Track refund protection widget loaded state (event-driven via callbacks)
  const [isRefundProtectionWidgetLoaded, setIsRefundProtectionWidgetLoaded] =
    useState(false);

  // Track refund protection form completion (event-driven via callbacks)
  // Starts as false (invalid) and becomes true when user opts in/out or widget errors
  const [isRefundProtectionFormCompleted, setIsRefundProtectionFormCompleted] =
    useState(false);

  // Scroll to refund protection section with a small delay for smooth UX
  // Only scrolls when checkout_scroll_to_invalid_section feature flag is enabled
  const scrollToRefundProtection = useCallback(() => {
    if (!isCheckoutScrollEnabled) return;

    setTimeout(() => {
      refundProtectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [isCheckoutScrollEnabled]);

  // Scroll to payment methods section with a small delay for smooth UX
  // Only scrolls when checkout_scroll_to_invalid_section feature flag is enabled
  const scrollToPaymentMethods = useCallback(() => {
    if (!isCheckoutScrollEnabled) return;

    setTimeout(() => {
      paymentMethodsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [isCheckoutScrollEnabled]);

  // Compute refund protection validation
  // Only valid when:
  // 1. Widget not shown (always valid), OR
  // 2. User has completed the form (opted in/out), OR
  // 3. Widget failed to load (onError sets this to true)
  const isRefundProtectionValid = shouldShowRefundProtectionWidget
    ? isRefundProtectionFormCompleted
    : true; // Widget not shown: always valid

  // Scroll to first invalid section when validation is triggered (user clicks purchase button)
  useEffect(() => {
    const handleValidationTriggered = () => {
      // Check refund protection first (if eligible and not completed)
      if (shouldShowRefundProtectionWidget && !isRefundProtectionValid) {
        scrollToRefundProtection();
      }
      // Then check payment method (if refund protection is valid but payment is not)
      else if (!paymentTypeValid) {
        scrollToPaymentMethods();
      }
    };

    window.addEventListener(
      "checkout:validation-triggered",
      handleValidationTriggered
    );
    return () =>
      window.removeEventListener(
        "checkout:validation-triggered",
        handleValidationTriggered
      );
  }, [
    shouldShowRefundProtectionWidget,
    isRefundProtectionValid,
    paymentTypeValid,
    scrollToRefundProtection,
    scrollToPaymentMethods,
  ]);

  const isReactNativeWebview = useMemo(
    () => checkForReactNativeAttribute(),
    []
  );

  // If react native webview, do not allow redirect.
  const allowRedirectValue = useMemo(
    () => !isReactNativeWebview && allowRedirect,
    [allowRedirect, isReactNativeWebview]
  );

  const { isLoggedIn } = useUserSessionAuthState();
  const { usePaginated } = useUserBasedPaymentMethodService();
  const { data, isLoading: isLoadingPaymentMethods } = usePaginated({
    options: {
      // To fetch and see a user's payment methods, they must be logged in
      // and the venue must be using payment intents
      enabled:
        Boolean(isLoggedIn) && venue && !venue.use_stripe_payment_intents,
    },
  });
  const flattenedData = data ? getFlattenedData(data) : [];
  // Data is biased towards cards, but endpoint suggests any payment method
  const eligibleCards = flattenedData.filter((pm) => {
    if (
      publicBasket?.paymentGateway &&
      pm.payment_gateway_id === publicBasket.paymentGateway?.id
    ) {
      return true;
    }

    return false;
  });
  const hasSavedCards = Boolean(eligibleCards.length);

  // Compute counts of cards by last4 and type for expiration display
  const duplicateCountMap = useMemo(() => {
    const map = new Map<string, number>();
    eligibleCards.forEach((pm) => {
      const key = `${pm.card_last4}-${pm.card_type}`;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [eligibleCards]);

  // Build dynamic payment options list
  const paymentOptions = useMemo(() => {
    const options: PaymentTypeOption[] = [];

    // Always add manual card entry as the first option
    options.push({
      id: "manual_card_entry",
      paymentType: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      subtype: PaymentSubtype.MANUAL_CARD_ENTRY,
    });

    // Add saved cards
    eligibleCards.forEach((card) => {
      options.push({
        id: `saved_card_${card.id}`,
        paymentType: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
        subtype: PaymentSubtype.SAVED_CARD,
        savedPaymentMethodId: card.id as number,
        savedPaymentMethod: card,
      });
    });

    // Add Affirm if eligible
    const isAffirmEligible =
      enabledPaymentOptions[PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id] &&
      allowRedirectValue &&
      convertPriceStringToInteger(publicBasket.totalPrice) >= 5000;

    if (
      isAffirmEligible ||
      publicBasket.paymentType === PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id
    ) {
      options.push({
        id: "affirm",
        paymentType: PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
        subtype: PaymentSubtype.AFFIRM,
      });
    }

    return options;
  }, [
    eligibleCards,
    enabledPaymentOptions,
    allowRedirectValue,
    publicBasket.totalPrice,
    publicBasket.paymentType,
  ]);

  /**
   * Determine if Wallets are supported based on the payment type.
   * This is implemented due to our technical limitiation that a PAYMENT_TYPE must be a correct payment type for Wallets (currently CREDIT),
   * and the technical limitation of Wallet elements often preventing you from asynchronous logic during the wallet flow. This means that our systems
   * must be prepared ahead of time before even allowing a user to begin a wallet flow.
   *
   * Until the payments/baskets architecture is changed to allow simultaneous payment types going at the same time, this is necessary.
   */
  const setWalletSupport = useCallback((paymentType: number) => {
    // If payment type is not credit, prevent wallet flows
    if (paymentType !== PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id) {
      setIsWalletSupported(false);
    } else {
      setIsWalletSupported(true);
    }
  }, []);

  const handlePaymentTypeChange = useCallback(
    async (
      paymentOption: PaymentTypeOption,
      paymentTypeOnClick?: () => Promise<void> | void
    ) => {
      const prevPaymentOptionId = selectedPaymentOptionId;

      // Optimistically select the proper payment type
      setSelectedPaymentOptionId(paymentOption.id);

      // Determine wallet support based on paymentType
      setWalletSupport(paymentOption.paymentType);

      try {
        // Always update payment type
        logAppInfo(`Updating payment type: ${paymentOption.paymentType}`, {
          category: "Payment",
        });
        await paymentTypeOnClick?.();
      } catch (e) {
        // On error of basket sync, revert our optimistic selection
        setSelectedPaymentOptionId(prevPaymentOptionId);

        // When reverting wallet support, fallback to CREDIT (wallet supported) if prevPaymentOptionId is nullish (no previous selection)
        const prevOption = paymentOptions.find(
          (opt) => opt.id === prevPaymentOptionId
        );
        const fallbackPaymentType =
          prevOption?.paymentType ?? PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id;
        setWalletSupport(fallbackPaymentType);

        logAppInfo(
          `Error updating payment type: ${paymentOption.paymentType}, fallback to ${fallbackPaymentType}`,
          {
            category: "Payment",
          }
        );
      }
    },
    [selectedPaymentOptionId, setWalletSupport, paymentOptions]
  );

  const handlePaymentValidationChange = (
    paymentOptionId: string,
    isValid: boolean
  ) => {
    setPaymentValidationState((prevState) => ({
      ...prevState,
      [paymentOptionId]: isValid,
    }));
  };

  const handleWalletPaymentSuccess = (purchaseData: PurchaseData) => {
    handleSetPurchaseData(purchaseData);
  };

  /**
   * useEffect responsible for calling validation callback
   * Includes both payment validation AND refund protection validation (if eligible)
   * Also updates RHF form state for granular inline error display
   */
  useEffect(() => {
    if (publicBasket.isFree) {
      // No payment required, no billing address required. Instantly valid
      setValue(VALIDATION_FIELDS.paymentType, true);
      setValue(VALIDATION_FIELDS.refundProtection, true);
      onValid();
      return;
    }

    if (selectedPaymentOptionId !== undefined) {
      const isPaymentTypeValid =
        paymentValidationState[selectedPaymentOptionId] ?? false;

      // Update RHF payment type validation state
      setValue(VALIDATION_FIELDS.paymentType, isPaymentTypeValid);

      // Update RHF refund protection validation state
      setValue(VALIDATION_FIELDS.refundProtection, isRefundProtectionValid);

      // Only factor in refund protection if widget is being shown
      const isValid = shouldShowRefundProtectionWidget
        ? isPaymentTypeValid && isRefundProtectionValid
        : isPaymentTypeValid;

      if (isValid) {
        onValid();
      } else {
        onInvalid();
      }
    } else {
      // No payment option selected yet
      setValue(VALIDATION_FIELDS.paymentType, false);
      onInvalid();
    }
  }, [
    selectedPaymentOptionId,
    paymentValidationState,
    publicBasket.isFree,
    shouldShowRefundProtectionWidget,
    isRefundProtectionValid,
  ]);

  useEffect(() => {
    if (
      isReactNativeWebview &&
      !hasInitializedWebviewWithManualCardEntry.current
    ) {
      // In react native webview, automatically open manual credit card type
      // as it the initial available payment method.
      const manualCardOption = paymentOptions.find(
        (opt) => opt.subtype === PaymentSubtype.MANUAL_CARD_ENTRY
      );
      if (manualCardOption) {
        hasInitializedWebviewWithManualCardEntry.current = true;
        handlePaymentTypeChange(manualCardOption);
      }
    }
  }, [isReactNativeWebview, paymentOptions, handlePaymentTypeChange]);

  // Add a timer for our manual card entry to automatically show up,
  // if our asynchronously loaded data was not loaded in time.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFallbackTimerTriggered(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const hasVenueLoaded = Boolean(venue?.id);

  // Break down the readiness conditions for better readability
  const isWalletReadyOrNotSupported =
    (isWalletSupported && isWalletReady) ||
    !isWalletSupported ||
    isReactNativeWebview;

  const arePaymentMethodsReady =
    !isLoggedIn || // Not logged in, so no saved payment methods to load
    !isLoadingPaymentMethods; // Payment methods finished loading

  const isReadyForPayments =
    (isWalletReadyOrNotSupported && arePaymentMethodsReady && hasVenueLoaded) ||
    fallbackTimerTriggered;

  // Use the payment option ID directly as the accordion value (v3 API uses string[])
  const selectedAccordionValue = useMemo(() => {
    if (!selectedPaymentOptionId) return undefined;
    return [selectedPaymentOptionId];
  }, [selectedPaymentOptionId]);

  const doesGatewaySupportSavingCards =
    // We currently only support system gateways
    Boolean(publicBasket?.paymentGateway?.is_system) &&
    // We currently only support CAD cards
    Boolean(publicBasket?.paymentGateway?.currency === CURRENCY_MAP.CAD.id) &&
    // We currently only support saving cards on Stripe
    Boolean(
      publicBasket?.paymentGateway?.gateway_type ===
        PAYMENT_GATEWAY_MAP.GATEWAY_STRIPE.id
    );

  const doesVenueSupportSavingCards =
    // We currently only support saving cards using Charges API
    !venue?.use_stripe_payment_intents;

  const supportsSavingCards =
    Boolean(isLoggedIn) &&
    doesGatewaySupportSavingCards &&
    doesVenueSupportSavingCards;

  // Show errors when field is touched OR validation attempted (hybrid approach)
  // This is especially important for selections like payment type which don't naturally "blur"
  const shouldShowPaymentTypeError =
    (isPaymentTypeTouched || validationAttempted) &&
    !paymentTypeValid &&
    Boolean(paymentTypeErrorMessage);
  const shouldShowRefundProtectionError =
    (isRefundProtectionTouched || validationAttempted) &&
    !refundProtectionValid &&
    !isRefundProtectionValid &&
    Boolean(refundProtectionErrorMessage);

  return (
    <>
      {/* Refund Protection Section - ref used for scroll-to functionality on validation */}
      <Box ref={refundProtectionRef}>
        <ErrorBoundary fallback={null}>
          <Box marginBottom={isRefundProtectionWidgetLoaded ? "md" : "0"}>
            {/* Mount PublicRefundProtection for analytics tracking and event-driven validation
            Callbacks are triggered by the tg widget's loaded/opt-in/opt-out/error events */}
            <PublicRefundProtection
              onLoaded={() => {
                // Widget successfully loaded - track for spacing and UI purposes
                setIsRefundProtectionWidgetLoaded(true);
              }}
              onFormComplete={() => {
                // User opted in/out - form interaction complete, mark as valid
                setIsRefundProtectionFormCompleted(true);
                // Scroll to payment methods section after user completes refund protection selection
                scrollToPaymentMethods();
              }}
              onError={() => {
                // Widget failed to load - don't block checkout
                setIsRefundProtectionWidgetLoaded(false);
                setIsRefundProtectionFormCompleted(true);
              }}
              isEligible={isBasketEligibleForProtection}
              shouldShow={shouldShowRefundProtectionWidget}
            />
          </Box>
          {/* Display refund protection validation error from RHF inline */}
          {shouldShowRefundProtectionError && (
            <Box mt="xs">
              <Text fontSize="sm" color="red.500" role="alert">
                {refundProtectionErrorMessage}
              </Text>
            </Box>
          )}
        </ErrorBoundary>
      </Box>

      {/* in case of isWalletSupported = false there is no need for Skeleton */}
      {!isReadyForPayments && isWalletSupported && (
        <Box display="flex" flexDirection="column" gap="md">
          <Box>
            <Skeleton width="element.8xl" height="element.xl" m="auto" />
          </Box>
          <Skeleton width="100%" height="element.3xl" />
          <Flex alignItems="center" flexDirection="row" gap="xl">
            <Box
              flex={1}
              marginTop="28px"
              marginBottom="28px"
              border="1px solid"
              borderColor="gray.300"
            />
            <Box
              flex={1}
              marginTop="28px"
              marginBottom="28px"
              border="1px solid"
              borderColor="gray.300"
            />
          </Flex>
          <Skeleton width="100%" height="element.3xl" />
        </Box>
      )}

      {/* Wrapper for all payment methods - used for scroll-to-payment functionality */}
      <Box ref={paymentMethodsRef}>
        <ErrorBoundary fallback={null}>
          <Box
            marginTop="md"
            marginBottom="md"
            display={
              // In react native webview, do not show express checkout.
              // Apple Pay and Google Pay do not work properly with react-native-webview
              isWalletReady && isWalletSupported && !isReactNativeWebview
                ? "block"
                : "none"
            }
          >
            <Flex alignItems="center" justifyContent="center">
              <Text fontSize="md" fontWeight={600} color="gray.600">
                {t("Express checkout")}
              </Text>
            </Flex>
            <Box mt="md" mb="md" minHeight="50px">
              <WalletElement
                onElementReady={() => setIsWalletReady(true)}
                onElementError={() => setIsWalletReady(false)}
                onComponentUnmount={() => setIsWalletReady(false)}
                onPaymentSuccess={handleWalletPaymentSuccess}
                {...(venue?.allowed_shipping_countries?.length && {
                  allowedShippingCountries: venue?.allowed_shipping_countries,
                })}
              />
            </Box>
            <Flex alignItems="center">
              <Box
                flex={1}
                marginTop="28px"
                marginBottom="28px"
                border="1px solid"
                borderColor="gray.300"
              />
              <Box>
                <Text
                  marginLeft="md"
                  marginRight="md"
                  fontSize="md"
                  color="gray.600"
                >
                  {t("OR")}
                </Text>
              </Box>
              <Box
                flex={1}
                marginTop="28px"
                marginBottom="28px"
                border="1px solid"
                borderColor="gray.300"
              />
            </Flex>
          </Box>
        </ErrorBoundary>

        <Box visibility={isReadyForPayments ? "visible" : "hidden"}>
          {/* TODO drop accordions completely */}
          <PaymentTypeAccordion
            value={selectedAccordionValue}
            onChange={() => {
              // noop
            }}
          >
            {paymentOptions.map((option) => {
              if (option.subtype === PaymentSubtype.MANUAL_CARD_ENTRY) {
                return (
                  <CardPaymentTypeManualCardEntry
                    key={option.id}
                    value={option.id}
                    onSelect={() => {
                      // TODO: move all this to a PublicCardPaymentTypeManualCardEntry wrapper component
                      const func = async () => {
                        const currentPaymentType = publicBasket.paymentType;
                        await publicBasket.updatePaymentType(
                          PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
                          {
                            shouldPersist: false,
                          }
                        );
                        await publicBasket.updatePaymentMethod(null, {
                          shouldPersist: false,
                        });
                        // Only save the refund protection token if the payment type is not changing
                        await publicBasket.save({
                          withProtectionToken:
                            currentPaymentType ===
                            PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
                        });

                        // Don't reset billing address form when switching to manual entry.
                        // There is a greater positive impact on convenience than a negative impact on having to change every field entry
                        // since a saved card should be a user's own personal information anyway
                        // checkoutForms.billingAddressForm.reset();
                      };
                      handlePaymentTypeChange(option, func);
                    }}
                    onValidationChange={(isValid) =>
                      handlePaymentValidationChange(option.id, isValid)
                    }
                    shouldSaveCard={publicBasket.saveUserPaymentMethod}
                    onToggleSaveCard={(shouldSaveCard) => {
                      publicBasket.updateSaveUserPaymentMethod(shouldSaveCard, {
                        shouldPersist: false,
                      });
                    }}
                    isDisabled={isBasketUpdating}
                    manualCardEntryForm={checkoutForms.manualCardEntryForm}
                    addressForm={checkoutForms.billingAddressForm}
                    userHasSavedCards={hasSavedCards}
                    supportsSavingCards={supportsSavingCards}
                    onBillingAddressChange={(billingAddress) => {
                      onBillingAddressChange(billingAddress);
                    }}
                  />
                );
              }

              if (option.subtype === PaymentSubtype.SAVED_CARD) {
                if (!option.savedPaymentMethod) return null;
                const pm = option.savedPaymentMethod;
                const key = `${pm.card_last4}-${pm.card_type}`;
                const showExpiration = (duplicateCountMap.get(key) ?? 0) > 1;
                return (
                  <CardPaymentTypeSavedCard
                    key={option.id}
                    value={option.id}
                    onSelect={(paymentMethod) => {
                      // TODO: move all this to a PublicCardPaymentTypeSavedCard wrapper component
                      const func = async () => {
                        const currentPaymentType = publicBasket.paymentType;
                        await publicBasket.updatePaymentType(
                          PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
                          {
                            shouldPersist: false,
                          }
                        );
                        await publicBasket.updatePaymentMethod(
                          paymentMethod?.id as number,
                          {
                            shouldPersist: false,
                          }
                        );
                        // Only save the refund protection token if the payment type is not changing
                        await publicBasket.save({
                          withProtectionToken:
                            currentPaymentType ===
                            PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
                        });

                        // Populate billing address form with saved payment method's address
                        if (paymentMethod.billing_address) {
                          const fields = {
                            info_home_address_street_name:
                              paymentMethod.billing_address.street_address,
                            info_home_address_unit_number:
                              paymentMethod.billing_address.unit_number,
                            info_home_address_city:
                              paymentMethod.billing_address.city,
                            info_home_address_province:
                              paymentMethod.billing_address.province,
                            info_home_address_postal:
                              paymentMethod.billing_address.postal,
                            info_home_address_country:
                              paymentMethod.billing_address.country,
                          };

                          // Set all fields at once
                          Object.entries(fields).forEach(([key, value]) => {
                            if (value) {
                              checkoutForms.billingAddressForm.setValue(
                                `checkout_billing_address_form_${key}`,
                                value
                              );
                            }
                          });

                          // Trigger validation
                          checkoutForms.billingAddressForm.trigger();
                        }

                        // Notify shipping address form of the billing address
                        const billingAddress = {
                          info_home_address_country:
                            paymentMethod.billing_address?.country,
                        };
                        onBillingAddressChange(billingAddress);
                      };
                      handlePaymentTypeChange(option, func);
                      handlePaymentValidationChange(option.id, true);
                    }}
                    isDisabled={isBasketUpdating}
                    paymentMethod={pm}
                    showExpiration={showExpiration}
                  />
                );
              }

              if (option.subtype === PaymentSubtype.AFFIRM) {
                return (
                  <AffirmPaymentType
                    key={option.id}
                    value={option.id}
                    onSelect={(func) => {
                      handlePaymentTypeChange(option, func);
                    }}
                    onValidationChange={(isValid) =>
                      handlePaymentValidationChange(option.id, isValid)
                    }
                    isDisabled={isBasketUpdating}
                    publicBasket={publicBasket}
                    addressForm={checkoutForms.billingAddressForm}
                    onBillingAddressChange={(billingAddress) => {
                      onBillingAddressChange(billingAddress);
                    }}
                  />
                );
              }

              return null;
            })}
          </PaymentTypeAccordion>
          {/* Display payment type validation error from RHF inline */}
          {shouldShowPaymentTypeError && (
            <Box mt="xs">
              <Text fontSize="sm" color="red.500" role="alert">
                {paymentTypeErrorMessage}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
